import * as React from "react";
import { AlertTriangle, History, Link2Off, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

type Target = "contact_company" | "deal_participant" | "deal_primary_contact" | "deal_primary_company";
type Decision = "association_wrong" | "relationship_ended";

export default function AssociationDecisionDialog({
  open,
  onOpenChange,
  target,
  associationId,
  label,
  onApplied,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: Target;
  associationId: number | null;
  label: string;
  onApplied?: () => void;
}) {
  const [decision, setDecision] = React.useState<Decision>("association_wrong");
  const [reason, setReason] = React.useState("");
  const [preview, setPreview] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setPreview(null);
      setReason("");
      setError(null);
      setDecision("association_wrong");
      return;
    }
    if (!associationId) return;
    setLoading(true);
    apiRequest({
      url: "/api/association-decisions/preview",
      method: "POST",
      data: { target, associationId, decision, reason: "preview" },
      isMutation: false,
      notifySuccess: false,
    }).then(setPreview).catch((cause: any) => setError(cause?.message || "Anteprima non disponibile"))
      .finally(() => setLoading(false));
  }, [open, associationId, target, decision]);

  const apply = async () => {
    if (!associationId || reason.trim().length < 3) {
      setError("Indica il motivo della decisione.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await apiRequest("POST", "/api/association-decisions/apply", {
        target, associationId, decision, reason: reason.trim(),
      });
      onOpenChange(false);
      onApplied?.();
    } catch (cause: any) {
      setError(cause?.message || "Decisione non applicata");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent elevated className="max-w-lg overflow-y-auto rounded-lg">
        <DialogHeader>
          <DialogTitle>Decidi sull’associazione</DialogTitle>
          <DialogDescription>{label}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <Button type="button" variant={decision === "association_wrong" ? "default" : "outline"} className="h-auto justify-start gap-2 whitespace-normal text-left" onClick={() => setDecision("association_wrong")}>
              <Link2Off className="h-4 w-4 shrink-0" />
              <span><strong>Associazione errata</strong><small className="block opacity-80">Revoca il legame e ricalcola solo i derivati verificabili.</small></span>
            </Button>
            <Button type="button" variant={decision === "relationship_ended" ? "default" : "outline"} className="h-auto justify-start gap-2 whitespace-normal text-left" onClick={() => setDecision("relationship_ended")}>
              <History className="h-4 w-4 shrink-0" />
              <span><strong>Rapporto terminato</strong><small className="block opacity-80">Chiude il rapporto e conserva la storia.</small></span>
            </Button>
          </div>
          {loading && !preview ? <p className="text-sm text-muted-foreground">Calcolo impatto…</p> : null}
          {preview?.impact ? (
            <div className="space-y-1 border border-border bg-muted/20 p-3 text-sm">
              <p className="font-semibold">Prima di procedere</p>
              <p>Stanze da ricalcolare: <strong>{preview.impact.roomsToRecompute}</strong></p>
              <p>Email automatiche osservate: <strong>{preview.impact.automaticEmailAssociationsObserved}</strong></p>
              <p>Email manuali preservate: <strong>{preview.impact.manualAssociationsPreserved}</strong></p>
              {preview.impact.legacyDerivedAssociationsRequireReview ? <p className="flex gap-2 text-amber-700"><AlertTriangle className="h-4 w-4 shrink-0" />Derivati legacy senza provenienza completa: resteranno da verificare.</p> : null}
            </div>
          ) : null}
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Perché questa associazione è errata o terminata?" className="min-h-20 w-full border border-border bg-background p-2 text-sm" />
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={apply} disabled={loading || reason.trim().length < 3}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Applica decisione
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
